import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAuth } from 'pages/AuthProvider';
import { useUserInfo } from 'pages/UserProvider';

// setCompany swallows a failed spoof-token mint (no state change, no rejection), so a
// switch can silently never complete. Give up after this long rather than holding the
// caller's render forever.
export const SWITCH_TIMEOUT_MS = 10_000;

export type SwitchCompany = Readonly<{
  /** Switch into `companyId`. No-op if already there or that switch is already in flight. */
  switchTo: (companyId: string) => void;
  /** Stand down an in-flight switch's bail-out, e.g. when the caller is leaving anyway. */
  cancel: () => void;
}>;

/**
 * The one place a portal flow switches company on the user's behalf — auto-spoof for deep
 * links (`useSpoofFromUrl`), embedded presentations (`usePresentationsSpoof`) and lead links
 * (`useProspectOwnerSpoof`). Callers decide which company to switch into and what failure
 * looks like; this owns the company fetch, the in-flight guard and the bail-out timer.
 *
 * `onFailure` fires once if the company can't be fetched (unknown, or no access) or the
 * switch hasn't landed within SWITCH_TIMEOUT_MS. Pass a stable (memoized) callback.
 */
const useSwitchCompany = (onFailure: () => void): SwitchCompany => {
  const { company, setCompany } = useUserInfo();
  const {
    clients: { company: companyClient },
  } = useAuth();
  const currentCompanyId = company?.id;
  const requestedId = useRef<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancel = useCallback((): void => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const fail = useCallback((): void => {
    cancel();
    onFailure();
  }, [cancel, onFailure]);

  // The switch landed — stand the bail-out down.
  useEffect(() => {
    if (requestedId.current !== null && currentCompanyId === requestedId.current) {
      cancel();
    }
  }, [currentCompanyId, cancel]);

  useEffect(() => cancel, [cancel]);

  const switchTo = useCallback(
    (companyId: string): void => {
      if (companyId === currentCompanyId || companyId === requestedId.current) {
        return;
      }
      cancel();
      requestedId.current = companyId;
      timer.current = setTimeout(fail, SWITCH_TIMEOUT_MS);
      companyClient
        .getCompany({ id: companyId })
        .then((companyResponse) => {
          // A later switchTo supersedes this one.
          if (requestedId.current === companyId) {
            setCompany(companyResponse);
          }
        })
        .catch(() => {
          if (requestedId.current === companyId) {
            fail();
          }
        });
    },
    [currentCompanyId, companyClient, setCompany, cancel, fail]
  );

  return useMemo(() => ({ switchTo, cancel }), [switchTo, cancel]);
};

export default useSwitchCompany;
